import { useUserStore } from "@/stores/user";
import { clearIncomingPeerLogoutCookiesForCompany } from "@/cognito/crossAppLogoutCookie";
import {
  PENDING_COMPANY_EMAIL_INVITE_TOKEN_KEY,
  PENDING_SUBSCRIPTION_INVITE_SLUG_KEY,
} from "@/constants/subscriptionInvite";

/** Response body from `VUE_APP_SIGN_IN_URL` after Cognito-backed company login. */
export type CompanyRailsSignInResponse = {
  token: string;
  email?: string;
  has_company?: boolean;
  personal_info_required?: boolean | null;
  /** Rails `company_user.signup_source` — `carly` must not enter Manager SE onboarding. */
  signup_source?: string | null;
  user?: {
    uuid?: string;
    firstName?: string;
    lastName?: string;
    title?: string;
    switchable?: boolean;
  };
  company?: {
    name?: string;
    website?: string;
    program_linkedin_url?: string;
  };
  add_subscription?: boolean;
  custom_payout_fee?: number | null;
  cognito_user_id?: string;
};

/**
 * Hydrate Pinia from Rails company sign-in JSON: clear store, set session + peer cookies,
 * clear invite session keys (same as Cognito callback + bootstrap flows).
 */
export function applyCompanyRailsSignInResponseToStore(
  data: CompanyRailsSignInResponse,
  sessionEmailFallback: string
): void {
  const userStore = useUserStore();
  userStore.clearStore();
  clearIncomingPeerLogoutCookiesForCompany();
  userStore.setToken(data.token);
  {
    const id =
      typeof data.cognito_user_id === "string"
        ? data.cognito_user_id.trim()
        : "";
    if (id) userStore.setCognitoSub(id);
  }
  sessionStorage.removeItem(PENDING_SUBSCRIPTION_INVITE_SLUG_KEY);
  sessionStorage.removeItem(PENDING_COMPANY_EMAIL_INVITE_TOKEN_KEY);
  userStore.setEmail(data.email ?? sessionEmailFallback);
  if (data.user?.firstName) userStore.setFirstName(data.user.firstName);
  if (data.user?.lastName) userStore.setLastName(data.user.lastName);
  if (data.user?.title) userStore.setTitle(data.user.title);
  if (data.user?.switchable !== undefined) {
    userStore.setSwitchable(data.user.switchable);
  }
  userStore.setUserType("company_user");
  if (data.company?.name) userStore.setCompanyName(data.company.name);
  userStore.setCompanyWebsite(data.company?.website ?? "");
  if (data.company?.program_linkedin_url) {
    userStore.setProgramLinkedinUrl(data.company.program_linkedin_url);
  }
  if (data.add_subscription !== undefined) {
    userStore.setAddSubscription(data.add_subscription);
  }
  if (data.custom_payout_fee !== undefined) {
    userStore.setCustomPayoutFee(data.custom_payout_fee);
  }
}
